import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Upload, X, Files, User } from 'lucide-react';
import { toast } from 'sonner';
import { useDocuments } from '@/hooks/useDocuments';
import { useStudents } from '@/contexts';

interface DocumentUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DocumentFormData {
  name: string;
  description: string;
  category: string;
  files: File[];
  studentId: string;
  studentName: string;
}

export const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  isOpen,
  onClose
}) => {
  const { addDocument } = useDocuments();
  const { students } = useStudents();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<DocumentFormData>({
    name: '',
    description: '',
    category: 'general',
    files: [],
    studentId: '',
    studentName: ''
  });
  const [isUploading, setIsUploading] = useState(false);

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB per file
  const MAX_TOTAL_SIZE = 120 * 1024 * 1024; // 120MB total

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      console.log(`📂 Selected ${files.length} files`);
      
      // Validate file sizes
      const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        toast.error(`Some files exceed the 100MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
        return;
      }

      // Check total size
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        toast.error(`Total files size (${formatFileSize(totalSize)}) exceeds 120MB limit`);
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        files,
        name: prev.name || (files.length === 1 ? files[0].name.split('.')[0] : `${files.length} Documents`)
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      name: prev.files.length === 2 ? prev.files.filter((_, i) => i !== index)[0].name.split('.')[0] : prev.name
    }));
  };

  const handleStudentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedStudentId = event.target.value;
    const selectedStudent = students?.find(s => s.id.toString() === selectedStudentId);
    
    console.log('Student selected for document upload:', selectedStudentId, selectedStudent?.name);
    
    setFormData(prev => ({
      ...prev,
      studentId: selectedStudentId,
      studentName: selectedStudent ? selectedStudent.name : ''
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (formData.files.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    // Final validation before upload
    const totalSize = formData.files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      toast.error(`Total size (${formatFileSize(totalSize)}) exceeds 120MB limit`);
      return;
    }

    console.log(`🚀 Starting upload of ${formData.files.length} documents (100MB+ capacity)`);
    setIsUploading(true);

    try {
      let successCount = 0;
      
      for (const file of formData.files) {
        try {
          if (file.size > MAX_FILE_SIZE) {
            console.warn(`Skipping oversized file: ${file.name} (${formatFileSize(file.size)})`);
            continue;
          }

          const documentName = formData.files.length === 1 
            ? formData.name.trim() 
            : `${formData.name.trim()} - ${file.name.split('.')[0]}`;

          console.log(`📤 Uploading: ${documentName} (${formatFileSize(file.size)})`);

          const result = await addDocument({
            name: documentName,
            description: formData.description.trim(),
            category: formData.category,
            file,
            studentId: formData.studentId || undefined,
            studentName: formData.studentName || undefined
          });

          if (result) {
            successCount++;
            console.log(`✅ Document uploaded successfully: ${documentName}`);
          }
        } catch (error) {
          console.error(`Upload failed for file:`, error);
        }
      }

      if (successCount > 0) {
        const message = `Successfully uploaded ${successCount} document(s)${formData.studentName ? ` for ${formData.studentName}` : ''}! (100MB+ capacity)`;
        toast.success(message);
        console.log(`✅ Upload complete: ${successCount} documents`);
      } else {
        toast.error('Failed to upload documents');
      }

      // Reset form
      setFormData({
        name: '',
        description: '',
        category: 'general',
        files: [],
        studentId: '',
        studentName: ''
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onClose();
    } catch (error) {
      console.error('Upload process failed:', error);
      toast.error('Failed to upload documents');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFormData({
        name: '',
        description: '',
        category: 'general',
        files: [],
        studentId: '',
        studentName: ''
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const getTotalSize = () => {
    return formData.files.reduce((total, file) => total + file.size, 0);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col bg-gray-900 text-white border-gray-700">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg text-white">
            <FileText className="h-4 w-4" />
            Upload Documents (100MB+ Capacity)
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="student-select" className="flex items-center gap-2 text-sm font-medium text-white">
                <User className="h-3 w-3" />
                Assign to Student
              </Label>
              <select
                id="student-select"
                value={formData.studentId}
                onChange={handleStudentChange}
                className="w-full p-2 text-sm border border-gray-600 rounded-md bg-black text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isUploading}
              >
                <option value="">Select a student or leave unassigned</option>
                {students && students.length > 0 ? (
                  students.map((student) => (
                    <option key={student.id} value={student.id.toString()}>
                      {student.name} (ID: {student.id})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No students available</option>
                )}
              </select>
              {formData.studentName && (
                <p className="text-xs text-green-400">
                  ✅ Documents will be assigned to: {formData.studentName}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-upload" className="text-sm font-medium text-white">
                Select Files (Max 100MB per file, 120MB total)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file-upload"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.xls,.ppt,.pptx,.zip,.rar"
                  className="flex-1 text-sm bg-black text-white border-gray-600 focus:border-blue-500"
                  disabled={isUploading}
                  multiple
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="h-8 w-8 p-0 border-gray-600 text-white hover:bg-gray-700"
                >
                  <Upload className="h-3 w-3" />
                </Button>
              </div>
              
              {formData.files.length > 0 && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-white">
                    <Files className="h-3 w-3" />
                    Selected Files ({formData.files.length}) - {formatFileSize(getTotalSize())}
                    {getTotalSize() > MAX_TOTAL_SIZE && (
                      <span className="text-red-400">⚠️ Exceeds 120MB limit</span>
                    )}
                  </div>
                  <div className="max-h-20 overflow-y-auto space-y-1">
                    {formData.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-800 p-2 rounded text-xs">
                        <span className="truncate flex-1 text-white">{file.name}</span>
                        <div className="flex items-center gap-1">
                          <span className={`${file.size > MAX_FILE_SIZE ? 'text-red-400' : 'text-gray-300'}`}>
                            ({formatFileSize(file.size)})
                            {file.size > MAX_FILE_SIZE && ' ⚠️'}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            disabled={isUploading}
                            className="h-5 w-5 p-0 text-white hover:bg-gray-700"
                          >
                            <X className="h-2 w-2" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-name" className="text-sm font-medium text-white">
                Document Name{formData.files.length > 1 ? ' Prefix' : ''}
              </Label>
              <Input
                id="doc-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={formData.files.length > 1 ? "Enter prefix for documents" : "Enter document name"}
                disabled={isUploading}
                className="text-sm bg-black text-white border-gray-600 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-category" className="text-sm font-medium text-white">Category</Label>
              <select
                id="doc-category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full p-2 text-sm border border-gray-600 rounded-md bg-black text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isUploading}
              >
                <option value="general">General</option>
                <option value="certificate">Certificate</option>
                <option value="id">ID Document</option>
                <option value="academic">Academic</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-description" className="text-sm font-medium text-white">Description (Optional)</Label>
              <Textarea
                id="doc-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description for all documents"
                rows={2}
                disabled={isUploading}
                className="text-sm resize-none bg-black text-white border-gray-600 focus:border-blue-500"
              />
            </div>
          </form>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-700 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            size="sm"
            className="border-gray-600 text-white hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={formData.files.length === 0 || !formData.name.trim() || isUploading || getTotalSize() > MAX_TOTAL_SIZE}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isUploading ? 'Uploading...' : `Upload ${formData.files.length || ''} Document${formData.files.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
